import { useEffect, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuth, getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import {
  ENGAGEMENT_PIPELINE_LABELS,
  STATUS_BADGE_CLASSES,
  DASHBOARD_WIDGET_REGISTRY,
  DASHBOARD_SECTION_ORDER,
  DASHBOARD_SECTION_LABELS,
  formatMinorAmount,
  type DashboardSectionKey,
  type DashboardTemplateWidget,
  type DashboardOutput,
  type DashboardFullOutput,
  type DashboardItemRow,
  type DashboardEngagementRow,
  type DashboardEngagementTaskRow,
  type DashboardDeadlineWindow,
  type DashboardMeetingRow,
  type DashboardWorkloadRow,
} from '@1financial/shared';
import { SkeletonBlock } from '../components/Skeleton.js';
import { ArrowRight, ArrowUp, ArrowDown, CalendarDays, SlidersHorizontal } from 'lucide-react';
import { fmtDate } from '../lib/date.js';
import { useUserPreference } from '../lib/user-preferences.js';

export function DashboardPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [customising, setCustomising] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preferenceError, setPreferenceError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useUserPreference<DashboardSectionKey>('dashboard.activeSection', 'myWork', {
    validate: (value) => DASHBOARD_SECTION_ORDER.includes(value as DashboardSectionKey),
  });

  const { data, isLoading, error } = useQuery<DashboardOutput>({
    queryKey: ['dashboard'],
    queryFn: async () => {
      const res = await fetch('/v1/reports/dashboard', {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: DashboardOutput };
      return body.data;
    },
  });

  const dashboard = data;
  const widgets = dashboard?.layout.widgets ?? [];
  const defaultSection = dashboard?.layout.defaultSection ?? 'myWork';
  const visibleWidgets = widgets.filter((entry) => entry.visible && availableWidget(entry));
  const sections = DASHBOARD_SECTION_ORDER.filter((section) => visibleWidgets.some((entry) => DASHBOARD_WIDGET_REGISTRY[entry.key].section === section));
  const sectionKey = sections.join(',');
  useEffect(() => {
    if (!sections.includes(activeSection)) setActiveSection(sections.includes(defaultSection) ? defaultSection : sections[0] ?? defaultSection);
  }, [sectionKey, activeSection, defaultSection, setActiveSection]);

  if (isLoading) {
    return (
      <div className="space-y-6 w-full min-w-0">
        <header><h1 className="text-xl sm:text-2xl font-semibold text-white">Dashboard</h1><p className="text-sm text-muted mt-1">Loading…</p></header>
        <SkeletonBlock rows={3} />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">{[...Array(3)].map((_, i) => <div key={i} className="bg-bg-panel border border-border rounded-xl p-4 h-40 animate-pulse" />)}</div>
        <SkeletonBlock rows={4} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="w-full min-w-0">
        <h1 className="text-xl sm:text-2xl font-semibold text-white">Dashboard</h1>
        <p className="text-sm text-rose-300 mt-2">Failed to load dashboard: {error?.message ?? 'unknown error'}</p>
      </div>
    );
  }

  const firstName = user?.displayName?.split(' ')[0] ?? user?.email;

  async function persist(next: DashboardTemplateWidget[]) {
    setSaving(true); setPreferenceError(null);
    try {
      const res = await fetch('/v1/reports/dashboard/preferences', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify({
          hidden: next.filter((entry) => !entry.visible).map((entry) => entry.key),
          order: next.map((entry) => entry.key),
          listLimits: Object.fromEntries(next.map((entry) => [entry.key, entry.listLimit])),
        }),
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    } catch (e) {
      setPreferenceError(e instanceof Error ? e.message : 'Could not save dashboard preferences');
    } finally { setSaving(false); }
  }

  function availableWidget(entry: DashboardTemplateWidget): boolean {
    if (!dashboard) return false;
    if (entry.key === 'myHours') return dashboard.view === 'my-day';
    if (entry.key === 'meetings') return true;
    if (entry.key === 'proposalPipeline') return dashboard.view === 'full' && dashboard.pipeline.proposals !== null;
    if (entry.key === 'invoiceStatus') return dashboard.view === 'full' && dashboard.invoiceStatus !== null;
    if (entry.key === 'receivablesSummary') return dashboard.view === 'full' && dashboard.receivables !== null;
    if (['teamExceptions', 'teamWorkload', 'teamHours', 'engagementPipeline', 'attendanceExceptions', 'goalProgress'].includes(entry.key)) return dashboard.view === 'full';
    return true;
  }
  function renderWidget(entry: DashboardTemplateWidget): ReactNode {
    if (!dashboard) return null;
    switch (entry.key) {
      case 'myTasks': return <MyTasksSection myWork={dashboard.myWork} limit={entry.listLimit} />;
      case 'myAssignments': return <MyWorkSection myWork={dashboard.myWork} limit={entry.listLimit} />;
      case 'attention': return <DeadlineLadderSection overdue={dashboard.deadlineLadder.overdue} today={dashboard.deadlineLadder.today} unscheduled={dashboard.deadlineLadder.unscheduled} limit={entry.listLimit} />;
      case 'upcoming': return <UpcomingSection week={dashboard.deadlineLadder.week} month={dashboard.deadlineLadder.month} limit={entry.listLimit} />;
      case 'teamExceptions': return dashboard.view === 'full' ? <TeamExceptionsSection rows={dashboard.teamExceptions.rows} overdueBefore={dashboard.deadlineLadder.overdue.dueBefore} limit={entry.listLimit} /> : null;
      case 'teamWorkload': return dashboard.view === 'full' ? <WorkloadSection workload={dashboard.workload} limit={entry.listLimit} /> : null;
      case 'teamHours': return dashboard.view === 'full' ? <TeamHoursSection teamHours={dashboard.teamHours} limit={entry.listLimit} missingTime={dashboard.teamExceptions.rows.filter((row) => row.noTimeToday)} /> : null;
      case 'engagementPipeline': return dashboard.view === 'full' ? <EngagementPipelineSection engagements={dashboard.pipeline.engagements} limit={entry.listLimit} /> : null;
      case 'proposalPipeline': return dashboard.view === 'full' && dashboard.pipeline.proposals ? <ProposalPipelineSection proposals={dashboard.pipeline.proposals} limit={entry.listLimit} /> : null;
      case 'invoiceStatus': return dashboard.view === 'full' && dashboard.invoiceStatus ? <InvoiceStatusSection invoices={dashboard.invoiceStatus} limit={entry.listLimit} /> : null;
      case 'receivablesSummary': return dashboard.view === 'full' && dashboard.receivables ? <ReceivablesSummarySection summary={dashboard.receivables.summary} clients={dashboard.receivables.clients} limit={entry.listLimit} /> : null;
      case 'goalProgress': return dashboard.view === 'full' ? <GoalProgressSection goals={dashboard.goalProgress} limit={entry.listLimit} /> : null;
      case 'attendanceExceptions': return dashboard.view === 'full' ? <AttendanceExceptionsSection attendance={dashboard.attendance} limit={entry.listLimit} /> : null;
      case 'meetings': return <MeetingsSection meetings={dashboard.todaysMeetings} limit={entry.listLimit} />;
      case 'myHours': return dashboard.view === 'my-day' ? <MyHoursSection today={dashboard.myHours.today} week={dashboard.myHours.week} /> : null;
    }
  }
  const activeWidgets = visibleWidgets.filter((entry) => DASHBOARD_WIDGET_REGISTRY[entry.key].section === activeSection);

  return (
    <div className="space-y-6 w-full min-w-0">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-white">Good {timeOfDay()}, {firstName}</h1>
          <p className="text-sm text-muted mt-1">Here's what needs attention today.</p>
        </div>
        <button type="button" onClick={() => setCustomising((value) => !value)} className="inline-flex items-center gap-2 text-xs text-muted hover:text-white border border-border hover:border-accent rounded-lg px-3 py-2">
          <SlidersHorizontal size={14} /> {customising ? 'Done' : 'Customise'}
        </button>
      </header>

      {customising && (
        <section className="bg-bg-panel border border-border rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-muted">Your overrides are saved on the server. Required widgets cannot be hidden.</p>
            <button type="button" disabled={saving} onClick={async () => { setSaving(true); await fetch('/v1/reports/dashboard/preferences', { method: 'DELETE', headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } }); await queryClient.invalidateQueries({ queryKey: ['dashboard'] }); setSaving(false); }} className="text-xs text-accent hover:underline flex-shrink-0">Reset to role default</button>
          </div>
          {preferenceError && <p className="text-xs text-rose-400">{preferenceError}</p>}
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {widgets.map((entry, index) => (
              <li key={entry.key} className="flex items-center justify-between gap-2 border border-border rounded-lg px-3 py-2 text-sm">
                <label className="flex items-center gap-2 text-white">
                  <input type="checkbox" checked={entry.visible} disabled={entry.required || saving} onChange={() => void persist(widgets.map((widget) => widget.key === entry.key ? { ...widget, visible: !widget.visible } : widget))} />
                  {DASHBOARD_WIDGET_REGISTRY[entry.key].label}
                </label>
                <span className="flex gap-1">
                  <button type="button" aria-label={`Move ${DASHBOARD_WIDGET_REGISTRY[entry.key].label} up`} disabled={index === 0 || saving} onClick={() => { const next = [...widgets]; [next[index], next[index - 1]] = [next[index - 1]!, next[index]!]; void persist(next); }} className="text-muted hover:text-white disabled:opacity-30"><ArrowUp size={14} /></button>
                  <button type="button" aria-label={`Move ${DASHBOARD_WIDGET_REGISTRY[entry.key].label} down`} disabled={index === widgets.length - 1 || saving} onClick={() => { const next = [...widgets]; [next[index], next[index + 1]] = [next[index + 1]!, next[index]!]; void persist(next); }} className="text-muted hover:text-white disabled:opacity-30"><ArrowDown size={14} /></button>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {sections.length > 0 && (
        <nav className="flex gap-1 overflow-x-auto border-b border-border pb-2" aria-label="Dashboard sections">
          {sections.map((section) => (
            <button key={section} onClick={() => setActiveSection(section)} className={`px-3 py-2 rounded-lg text-sm whitespace-nowrap ${activeSection === section ? 'bg-bg-hover text-white' : 'text-muted hover:text-white'}`}>
              {DASHBOARD_SECTION_LABELS[section]}
            </button>
          ))}
        </nav>
      )}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {activeWidgets.map((entry) => <div key={entry.key} className={entry.size === 'full' ? 'xl:col-span-2 min-w-0' : 'min-w-0'}>{renderWidget(entry)}</div>)}
      </div>
    </div>
  );
}

/* ─── Today's meetings ─────────────────────────────────────────────────────── */

function MeetingsSection({ meetings, limit }: { meetings: DashboardMeetingRow[]; limit: number }) {
  return (
    <section className="bg-bg-panel border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-medium text-white flex items-center gap-2">
          <CalendarDays size={15} className="text-muted" />
          Today's meetings <span className="text-xs text-muted font-normal">· {meetings.length}</span>
        </h2>
        <Link to="/meetings" className="text-xs text-accent hover:underline inline-flex items-center gap-1">
          Open calendar <ArrowRight size={12} />
        </Link>
      </div>
      {meetings.length === 0 && <p className="text-sm text-muted italic">No meetings today. Use Open calendar to view upcoming meetings.</p>}
      <ul className="divide-y divide-border">
        {meetings.slice(0, limit).map((m) => (
          <li key={m.id} className="py-2.5 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <Link to="/meetings" className="text-sm text-white hover:text-accent block truncate">{m.title}</Link>
              <p className="text-xs text-muted truncate">
                {new Date(m.startAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} · {m.durationMin} min
                {m.clientName ? ` · ${m.clientName}` : ''}
                {m.location ? ` · ${m.location}` : ''}
              </p>
            </div>
            {m.meetingUrl && (
              <a
                href={m.meetingUrl}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-accent hover:underline flex-shrink-0"
              >
                Join
              </a>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ─── My Tasks — unified personal queue ───────────────────────────────────── */

function MyTasksSection({ myWork, limit }: { myWork: DashboardOutput['myWork']; limit: number }) {
  const tasks = [
    ...myWork.items.map((item) => ({ id: item.id, title: item.serviceType, clientName: item.clientName, type: 'Compliance', status: item.status, dueDate: item.dueDate, to: `/compliance/${item.id}` })),
    ...myWork.engagements.map((engagement) => ({ id: engagement.id, title: engagement.title ?? 'Business setup', clientName: engagement.clientName, type: 'Engagement', status: ENGAGEMENT_PIPELINE_LABELS[engagement.pipeline] ?? engagement.pipeline, dueDate: engagement.endDate, to: `/engagements/${engagement.id}` })),
    ...myWork.engagementTasks.map((task) => ({ id: task.id, title: task.title, clientName: task.clientName, type: 'Checklist', status: task.status.replaceAll('_', ' '), dueDate: task.endDate, to: `/engagements/${task.engagementId}` })),
  ].sort((a, b) => {
    const aTime = a.dueDate ? new Date(a.dueDate).getTime() : Number.POSITIVE_INFINITY;
    const bTime = b.dueDate ? new Date(b.dueDate).getTime() : Number.POSITIVE_INFINITY;
    return aTime - bTime || a.title.localeCompare(b.title);
  });
  const visible = tasks.slice(0, limit);
  const total = myWork.count + myWork.engagementTaskCount;
  return (
    <section className="bg-bg-panel border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-medium text-white">My tasks <span className="text-xs text-muted font-normal">· Showing {visible.length} of {total}</span></h2>
        <Link to="/my-work" className="text-xs text-accent hover:underline inline-flex items-center gap-1">View all <ArrowRight size={12} /></Link>
      </div>
      {visible.length === 0 ? <p className="text-sm text-muted italic">Nothing assigned to you right now.</p> : (
        <ul className="divide-y divide-border">
          {visible.map((task) => (
            <li key={`${task.type}-${task.id}`} className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="min-w-0">
                <Link to={task.to} className="text-sm text-white hover:text-accent block truncate">{task.title}</Link>
                <p className="text-xs text-muted truncate">{task.clientName} · {task.type}</p>
              </div>
              <div className="sm:text-right flex-shrink-0">
                <div className="text-xs text-muted">{task.dueDate ? `${task.type === 'Checklist' ? 'Engagement end · ' : ''}${fmtDate(task.dueDate)}` : 'No due date'}</div>
                <span className="px-2 py-0.5 rounded text-xs bg-bg-hover text-white">{task.status}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ─── ① My Work ──────────────────────────────────────────────────────────── */

function MyWorkSection({ myWork, limit = 6 }: { myWork: DashboardOutput['myWork']; limit?: number }) {
  return (
    <section className="bg-bg-panel border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-medium text-white">
          My work <span className="text-xs text-muted font-normal">· {myWork.count} open{myWork.engagementTaskCount > 0 ? ` · ${myWork.engagementTaskCount} checklist tasks` : ''}</span>
        </h2>
        <Link to="/my-work" className="text-xs text-accent hover:underline inline-flex items-center gap-1">
          View all <ArrowRight size={12} />
        </Link>
      </div>
      {myWork.count === 0 ? (
        <p className="text-sm text-muted italic">Nothing assigned to you right now.</p>
      ) : (
        <div className="space-y-4">
          {myWork.engagements.length > 0 && (
            <div>
              <h3 className="text-xs text-muted uppercase tracking-wide mb-1">Engagements · {myWork.engagementCount}</h3>
              <ul className="divide-y divide-border">
                {myWork.engagements.slice(0, limit).map((engagement) => <EngagementRow key={engagement.id} engagement={engagement} />)}
              </ul>
            </div>
          )}
          {myWork.engagementTasks.length > 0 && (
            <div>
              <h3 className="text-xs text-muted uppercase tracking-wide mb-1">Engagement checklist · {myWork.engagementTaskCount}</h3>
              <ul className="divide-y divide-border">
                {myWork.engagementTasks.slice(0, limit).map((task) => <EngagementTaskRow key={task.id} task={task} />)}
              </ul>
            </div>
          )}
          {myWork.items.length > 0 && (
            <div>
              <h3 className="text-xs text-muted uppercase tracking-wide mb-1">Compliance · {myWork.complianceCount}</h3>
              <ul className="divide-y divide-border">
                {myWork.items.slice(0, limit).map((item) => <ItemRow key={item.id} item={item} overdue={isOverdue(item)} />)}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function EngagementTaskRow({ task }: { task: DashboardEngagementTaskRow }) {
  return (
    <li className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
      <div className="min-w-0">
        <Link to={`/engagements/${task.engagementId}`} className="text-sm text-white hover:text-accent block truncate">{task.title}</Link>
        <p className="text-xs text-muted truncate">{task.engagementTitle ?? 'Business setup'} · {task.clientName}</p>
      </div>
      <span className="px-2 py-0.5 rounded text-xs bg-bg-hover text-white flex-shrink-0">{task.status.replaceAll('_', ' ')}</span>
    </li>
  );
}

function EngagementRow({ engagement }: { engagement: DashboardEngagementRow }) {
  return (
    <li className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
      <div className="min-w-0">
        <Link to={`/engagements/${engagement.id}`} className="text-sm text-white hover:text-accent block truncate">
          {engagement.title ?? 'Business setup'}
        </Link>
        <p className="text-xs text-muted truncate">
          {engagement.clientName} {engagement.jurisdiction ? `· ${engagement.jurisdiction}` : ''}
        </p>
      </div>
      <div className="sm:text-right flex-shrink-0">
        <div className="text-xs text-muted">{engagement.endDate ? fmtDate(engagement.endDate) : 'No end date'}</div>
        <span className="px-2 py-0.5 rounded text-xs bg-bg-hover text-white">
          {ENGAGEMENT_PIPELINE_LABELS[engagement.pipeline] ?? engagement.pipeline}
        </span>
      </div>
    </li>
  );
}

/* ─── ② Deadline ladder ──────────────────────────────────────────────────── */

function DeadlineLadderSection({
  overdue,
  today,
  unscheduled,
  limit,
}: {
  overdue: DashboardDeadlineWindow;
  today: DashboardDeadlineWindow;
  unscheduled: { count: number; items: DashboardItemRow[] };
  limit: number;
}) {
  return (
    <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <LadderTile title="Overdue" sub="before today" window={overdue} accent="text-rose-400" limit={limit} />
      <LadderTile title="Due today" sub="today only" window={today} accent="text-amber-300" limit={limit} />
      <UnscheduledTile window={unscheduled} limit={limit} />
    </section>
  );
}

function LadderTile({
  title,
  sub,
  window: w,
  accent,
  limit,
}: {
  title: string;
  sub: string;
  window: DashboardDeadlineWindow;
  accent: string;
  limit: number;
}) {
  const params = new URLSearchParams();
  params.set('from', 'dashboard');
  params.set('open', 'true');
  if (w.dueAfter) params.set('dueDateAfter', w.dueAfter);
  params.set('dueDateBefore', w.dueBefore);
  params.set('sortBy', 'dueDate');
  const to = `/compliance?${params.toString()}`;
  return (
    <div className="bg-bg-panel border border-border rounded-xl p-4 flex flex-col">
      <Link to={to} className="group block">
        <div className="flex items-center justify-between">
          <div className="text-xs text-muted">{title}</div>
          <ArrowRight size={14} className="text-muted group-hover:text-accent group-hover:translate-x-0.5 transition-all" />
        </div>
        <div className={`text-xl sm:text-2xl font-semibold mt-1 ${accent}`}>{w.count}</div>
        <div className="text-xs text-muted mt-0.5">{sub}</div>
      </Link>
      {w.items.length > 0 && (
        <ul className="mt-3 pt-3 border-t border-border space-y-2 flex-1">
          {w.items.slice(0, limit).map((item) => (
            <li key={item.id} className="flex items-baseline justify-between gap-2 text-xs">
              <Link to={`/compliance/${item.id}`} className="min-w-0 truncate text-white hover:text-accent">
                {item.serviceType}
                <span className="text-muted"> · {item.clientName}</span>
              </Link>
              <span className={`flex-shrink-0 ${isOverdue(item) ? 'text-rose-400 font-medium' : 'text-muted'}`}>
                {fmtDate(item.dueDate)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {w.count > w.items.length && (
        <Link to={to} className="mt-2 text-xs text-accent hover:underline">
          +{w.count - w.items.length} more
        </Link>
      )}
    </div>
  );
}

function UnscheduledTile({ window: w, limit }: { window: { count: number; items: DashboardItemRow[] }; limit: number }) {
  const to = '/compliance?from=dashboard&open=true&noDueDate=true';
  return (
    <div className="bg-bg-panel border border-border rounded-xl p-4 flex flex-col">
      <Link to={to} className="group block">
        <div className="flex items-center justify-between">
          <div className="text-xs text-muted">No due date</div>
          <ArrowRight size={14} className="text-muted group-hover:text-accent group-hover:translate-x-0.5 transition-all" />
        </div>
        <div className="text-xl sm:text-2xl font-semibold mt-1 text-white">{w.count}</div>
        <div className="text-xs text-muted mt-0.5">open items needing a deadline</div>
      </Link>
      {w.items.length > 0 && (
        <ul className="mt-3 pt-3 border-t border-border space-y-2 flex-1">
          {w.items.slice(0, limit).map((item) => (
            <li key={item.id} className="flex items-baseline justify-between gap-2 text-xs">
              <Link to={`/compliance/${item.id}`} className="min-w-0 truncate text-white hover:text-accent">
                {item.serviceType}<span className="text-muted"> · {item.clientName}</span>
              </Link>
              <span className="flex-shrink-0 text-muted">No date</span>
            </li>
          ))}
        </ul>
      )}
      {w.count > w.items.length && (
        <Link to={to} className="mt-2 text-xs text-accent hover:underline">+{w.count - w.items.length} more</Link>
      )}
    </div>
  );
}

/* ─── Upcoming deadlines ─────────────────────────────────────────────────── */

function UpcomingSection({ week, month, limit }: { week: DashboardDeadlineWindow; month: DashboardDeadlineWindow; limit: number }) {
  const [tab, setTab] = useUserPreference<'week' | 'month'>('dashboard.upcomingTab', 'week', {
    validate: (value) => value === 'week' || value === 'month',
  });
  const w = tab === 'week' ? week : month;
  const params = new URLSearchParams({ from: 'dashboard', open: 'true', sortBy: 'dueDate' });
  if (w.dueAfter) params.set('dueDateAfter', w.dueAfter);
  params.set('dueDateBefore', w.dueBefore);
  const to = `/compliance?${params.toString()}`;
  return (
    <section className="bg-bg-panel border border-border rounded-xl p-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
        <h2 className="text-sm font-medium text-white">Upcoming <span className="text-xs text-muted font-normal">· {w.count}</span></h2>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-border overflow-hidden">
            {(['week', 'month'] as const).map((value) => (
              <button key={value} onClick={() => setTab(value)} className={`px-3 py-1.5 text-xs ${tab === value ? 'bg-bg-hover text-white' : 'text-muted hover:text-white'}`}>
                {value === 'week' ? 'This week' : 'Later this month'}
              </button>
            ))}
          </div>
          <Link to={to} className="text-xs text-accent hover:underline inline-flex items-center gap-1">View all <ArrowRight size={12} /></Link>
        </div>
      </div>
      {w.items.length === 0 ? (
        <p className="text-sm text-muted italic">No deadlines in this window.</p>
      ) : (
        <ul className="divide-y divide-border">
          {w.items.slice(0, limit).map((item) => <ItemRow key={item.id} item={item} overdue={isOverdue(item)} />)}
        </ul>
      )}
      {w.count > w.items.length && <Link to={to} className="mt-2 inline-block text-xs text-accent hover:underline">+{w.count - w.items.length} more</Link>}
    </section>
  );
}

/* ─── Team exceptions ────────────────────────────────────────────────────── */

function TeamExceptionsSection({ rows, overdueBefore, limit }: { rows: DashboardFullOutput['teamExceptions']['rows']; overdueBefore: string; limit: number }) {
  return (
    <section className="bg-bg-panel border border-border rounded-xl p-5">
      <h2 className="text-sm font-medium text-white mb-3">Team work <span className="text-xs text-muted font-normal">· {rows.length}</span></h2>
      {rows.length === 0 ? (
        <p className="text-sm text-muted italic">No team work exceptions today.</p>
      ) : (
        <ul className="divide-y divide-border">
          {rows.slice(0, limit).map((row) => (
            <li key={row.userId} className="py-3 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
              <span className="text-sm text-white flex-shrink-0">{row.displayName}</span>
              <span className="flex flex-wrap items-center gap-1.5 text-xs">
                {row.overdueItems > 0 && <Link to={`/compliance?from=dashboard&assigneeId=${row.userId}&open=true&dueDateBefore=${encodeURIComponent(overdueBefore)}`} className="px-2 py-0.5 rounded pill-danger">{row.overdueItems} overdue</Link>}
                {row.openItems >= 20 && <Link to={`/compliance?from=dashboard&assigneeId=${row.userId}&open=true`} className="px-2 py-0.5 rounded bg-bg-hover text-white">{row.openItems} open</Link>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ─── ③ Workload by person ───────────────────────────────────────────────── */

function WorkloadSection({ workload, limit }: { workload: DashboardFullOutput['workload']; limit: number }) {
  const groups: { label: string; rows: DashboardWorkloadRow[] }[] = [
    { label: 'Managers', rows: workload.managers },
    { label: 'Team Leads', rows: workload.teamLeads },
    { label: 'Staff', rows: workload.staff },
  ];
  const empty = groups.every((g) => g.rows.length === 0);
  return (
    <section className="bg-bg-panel border border-border rounded-xl p-5">
      <h2 className="text-sm font-medium text-white mb-3">Workload by person</h2>
      {empty ? (
        <p className="text-sm text-muted italic">No active team members.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {groups.map((g) =>
            g.rows.length === 0 ? null : (
              <div key={g.label}>
                <div className="text-xs text-muted uppercase tracking-wide mb-2">{g.label}</div>
                <ul className="space-y-1.5">
                  {g.rows.slice(0, limit).map((row) => (
                    <li key={row.userId}>
                      <Link
                        to={`/compliance?assigneeId=${row.userId}&open=true&sortBy=dueDate`}
                        className="group flex items-center justify-between gap-2 text-sm rounded-lg px-2 py-1.5 hover:bg-bg-hover/40"
                      >
                        <span className="text-white group-hover:text-accent truncate">{row.displayName}</span>
                        <span className="flex-shrink-0 text-xs space-x-2">
                          <span className="text-muted">{row.openItems} open</span>
                          {row.overdueItems > 0 && (
                            <span className="text-rose-400 font-medium">{row.overdueItems} overdue</span>
                          )}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ),
          )}
        </div>
      )}
    </section>
  );
}

/* ─── Engagement and Proposal ──────────────────────────────────────────────── */

function EngagementPipelineSection({ engagements, limit }: { engagements: DashboardFullOutput['pipeline']['engagements']; limit: number }) {
  return (
    <section className="bg-bg-panel border border-border rounded-xl p-4">
      <Link to="/engagements" className="group block">
        <div className="flex items-center justify-between">
          <div className="text-xs text-muted">Engagements in progress</div>
          <ArrowRight size={14} className="text-muted group-hover:text-accent group-hover:translate-x-0.5 transition-all" />
        </div>
        <div className="text-xl sm:text-2xl font-semibold mt-1 text-white">{engagements.inProgress}</div>
      </Link>
      <ul className="mt-3 pt-3 border-t border-border space-y-1.5">
        {engagements.byStage.slice(0, limit).map((s) => (
          <li key={s.stage}>
            <Link to={`/engagements?pipeline=${s.stage}`} className="group flex items-center justify-between gap-2 text-xs rounded px-1.5 py-1 hover:bg-bg-hover/40">
              <span className="text-muted group-hover:text-accent">{ENGAGEMENT_PIPELINE_LABELS[s.stage] ?? s.stage}</span>
              <span className={s.count > 0 ? 'text-white font-medium' : 'text-muted'}>{s.count}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ProposalPipelineSection({ proposals, limit }: { proposals: NonNullable<DashboardFullOutput['pipeline']['proposals']>; limit: number }) {
  return (
    <section className="bg-bg-panel border border-border rounded-xl p-4">
      <div className="text-xs text-muted">Proposals</div>
      <div className="flex items-end gap-6 mt-1">
        <Link to="/proposals?status=DRAFT" className="group">
          <div className="text-xl sm:text-2xl font-semibold text-white group-hover:text-accent">{proposals.draft}</div>
          <div className="text-xs text-muted">Draft</div>
        </Link>
        <Link to="/proposals?status=SENT" className="group">
          <div className="text-xl sm:text-2xl font-semibold text-sky-300 group-hover:text-accent">{proposals.sent}</div>
          <div className="text-xs text-muted">Sent</div>
        </Link>
      </div>
      {proposals.top.length > 0 && (
        <ul className="mt-3 pt-3 border-t border-border space-y-2">
          {proposals.top.slice(0, limit).map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-2 text-xs">
              <Link to={`/proposals/${p.id}`} className="min-w-0 truncate text-white hover:text-accent">{p.subject}<span className="text-muted"> · {p.clientName}</span></Link>
              <span className="flex-shrink-0 px-2 py-0.5 rounded text-xs bg-bg-hover text-white">{p.status}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ─── Invoices and Receivables ─────────────────────────────────────────────── */

function InvoiceStatusSection({ invoices, limit }: { invoices: NonNullable<DashboardFullOutput['invoiceStatus']>; limit: number }) {
  const cards = [
    { label: 'Draft', value: invoices.draft, status: 'DRAFT' },
    { label: 'Issued / sent', value: invoices.issued, status: 'SENT' },
    { label: 'Overdue', value: invoices.overdue, status: 'OVERDUE' },
    { label: 'Partially paid', value: invoices.partiallyPaid, status: 'PARTIALLY_PAID' },
    { label: 'Paid', value: invoices.paid, status: 'PAID' },
  ];
  return (
    <section className="bg-bg-panel border border-border rounded-xl p-5 space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {cards.map((card) => <Link key={card.label} to={`/invoices?status=${card.status}`} className="bg-bg-card border border-border rounded-lg p-3 hover:border-accent">
          <div className="text-xl font-semibold text-white">{card.value}</div><div className="text-xs text-muted">{card.label}</div>
        </Link>)}
      </div>
      <div>
        <h3 className="text-xs text-muted uppercase tracking-wide mb-2">Recent invoices</h3>
        {invoices.recent.length === 0 ? <p className="text-sm text-muted italic">No invoices yet.</p> : <ul className="divide-y divide-border">
          {invoices.recent.slice(0, limit).map((invoice) => <li key={invoice.id} className="py-2.5 flex items-center justify-between gap-3 text-sm">
            <Link to={`/invoices/${invoice.id}`} className="min-w-0 text-white hover:text-accent truncate">{invoice.invoiceNumber}<span className="text-muted"> · {invoice.clientName}</span></Link>
            <span className="text-xs text-muted flex-shrink-0">{invoice.status} · {formatMinorAmount(invoice.total, true)} {invoice.currency}</span>
          </li>)}
        </ul>}
      </div>
    </section>
  );
}

function ReceivablesSummarySection({ summary, clients, limit }: { summary: NonNullable<DashboardFullOutput['receivables']>['summary']; clients: NonNullable<DashboardFullOutput['receivables']>['clients']; limit: number }) {
  return (
    <section className="bg-bg-panel border border-border rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-white">Receivables</h2>
        <Link to="/invoices" className="text-xs text-accent hover:underline inline-flex items-center gap-1">Open receivables <ArrowRight size={12} /></Link>
      </div>
      {summary.length === 0 ? <p className="text-sm text-muted italic">No outstanding receivables.</p> : <>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          {summary.map((row) => <div key={row.currency} className="bg-bg-card border border-border rounded-lg p-3">
            <div className="text-xs text-muted">{row.currency} outstanding</div>
            <div className="text-xl font-semibold text-white">{formatMinorAmount(row.outstanding, true)}</div>
            <div className="text-xs text-rose-300 mt-1">Overdue {formatMinorAmount(row.overdue, true)}</div>
            <div className="text-xs text-muted mt-1">1–30 {formatMinorAmount(row.aging1_30, true)} · 31–60 {formatMinorAmount(row.aging31_60, true)} · 60+ {formatMinorAmount(row.aging61_90 + row.aging90Plus, true)}</div>
          </div>)}
        </div>
        <div>
          <h3 className="text-xs text-muted uppercase tracking-wide mb-2">By client</h3>
          <ul className="divide-y divide-border">
            {clients.slice(0, limit).map((client) => <li key={`${client.clientId}-${client.currency}`} className="py-2.5 flex items-center justify-between gap-3 text-sm">
              <span className="min-w-0 text-white truncate">{client.clientName}<span className="text-muted"> · {client.currency}</span></span>
              <span className="text-white flex-shrink-0">{formatMinorAmount(client.outstanding, true)}</span>
            </li>)}
          </ul>
        </div>
      </>}
    </section>
  );
}

/* ─── Goals ────────────────────────────────────────────────────────────────── */

function GoalProgressSection({ goals, limit }: { goals: DashboardFullOutput['goalProgress']; limit: number }) {
  return (
    <section className="bg-bg-panel border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-medium text-white">Goals</h2>
        <Link to="/goals" className="text-xs text-accent hover:underline inline-flex items-center gap-1">Open goals <ArrowRight size={12} /></Link>
      </div>
      {goals.length === 0 ? <p className="text-sm text-muted italic">No active goals.</p> : <ul className="space-y-3">
        {goals.slice(0, limit).map((goal) => {
          const percentage = goal.targetValue > 0 ? Math.min(100, Math.max(0, (goal.currentValue / goal.targetValue) * 100)) : 0;
          return <li key={goal.id}>
            <div className="flex items-center justify-between gap-3 text-sm mb-1">
              <span className="text-white truncate">{goal.title}</span>
              <span className="text-xs text-muted flex-shrink-0">{goal.currentValue} / {goal.targetValue} · {goal.period}</span>
            </div>
            <div className="h-2 rounded-full bg-bg-hover overflow-hidden"><div className="h-full bg-accent rounded-full" style={{ width: `${percentage}%` }} /></div>
            <div className="text-xs text-muted mt-1">{goal.scopeType} · {goal.metric}</div>
          </li>;
        })}
      </ul>}
    </section>
  );
}

/* ─── Attendance ───────────────────────────────────────────────────────────── */

function AttendanceExceptionsSection({ attendance, limit }: { attendance: DashboardFullOutput['attendance']; limit: number }) {
  const missingTime = attendance.missingCheckout.slice(0, limit);
  const pendingLeave = attendance.pendingLeave.slice(0, limit);
  return (
    <section className="bg-bg-panel border border-border rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-white">Attendance</h2>
        <Link to="/attendance" className="text-xs text-accent hover:underline inline-flex items-center gap-1">Open attendance <ArrowRight size={12} /></Link>
      </div>
      <div>
        <h3 className="text-xs text-muted uppercase tracking-wide mb-2">Missing checkout</h3>
        {missingTime.length === 0 ? <p className="text-sm text-muted italic">No missing checkouts.</p> : <ul className="space-y-1.5">
          {missingTime.map((row) => <li key={row.userId} className="text-sm text-white">{row.displayName}</li>)}
        </ul>}
      </div>
      <div>
        <h3 className="text-xs text-muted uppercase tracking-wide mb-2">Pending leave approvals</h3>
        {pendingLeave.length === 0 ? <p className="text-sm text-muted italic">No pending leave requests.</p> : <ul className="divide-y divide-border">
          {pendingLeave.map((leave) => <li key={leave.id} className="py-2 text-sm text-white">
            {leave.userName}<span className="text-xs text-muted"> · {leave.type} · {fmtDate(leave.fromDate)}–{fmtDate(leave.toDate)}</span>
          </li>)}
        </ul>}
      </div>
    </section>
  );
}

/* ─── ⑤ Team hours ───────────────────────────────────────────────────────── */

function TeamHoursSection({ teamHours, limit, missingTime = [] }: { teamHours: DashboardFullOutput['teamHours']; limit: number; missingTime?: DashboardFullOutput['teamExceptions']['rows'] }) {
  const maxToday = Math.max(8, ...teamHours.rows.map((r) => r.today));
  const maxWeek = Math.max(40, ...teamHours.rows.map((r) => r.week));
  return (
    <section className="bg-bg-panel border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-medium text-white">
          Team hours <span className="text-xs text-muted font-normal">· {formatHours(teamHours.totalToday)} today · {formatHours(teamHours.totalWeek)} this week</span>
        </h2>
        <Link to="/time" className="text-xs text-accent hover:underline inline-flex items-center gap-1">
          Time entries <ArrowRight size={12} />
        </Link>
      </div>
      {teamHours.rows.length === 0 ? (
        <p className="text-sm text-muted italic">No active team members.</p>
      ) : (
        <ul className="space-y-2">
          {teamHours.rows.slice(0, limit).map((row) => (
            <li key={row.userId} className="flex items-center gap-3 text-xs">
              <span className="w-32 flex-shrink-0 truncate text-white">
                {row.displayName}
                {row.today === 0 && (
                  <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-amber-400 align-middle" title="No time logged today" />
                )}
              </span>
              <span className="flex-1 flex items-center gap-2 min-w-0">
                <span className="h-2 rounded-full bg-bg-hover flex-1 overflow-hidden">
                  <span className="block h-full rounded-full bg-accent" style={{ width: `${Math.min(100, (row.today / maxToday) * 100)}%` }} />
                </span>
                <span className="w-12 flex-shrink-0 text-right text-muted">{formatHours(row.today)}</span>
              </span>
              <span className="flex-1 flex items-center gap-2 min-w-0">
                <span className="h-2 rounded-full bg-bg-hover flex-1 overflow-hidden">
                  <span className="block h-full rounded-full bg-accent/60" style={{ width: `${Math.min(100, (row.week / maxWeek) * 100)}%` }} />
                </span>
                <span className="w-12 flex-shrink-0 text-right text-muted">{formatHours(row.week)}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
      {missingTime.length > 0 && <div className="text-xs text-amber-300 mt-3">No time logged today: {missingTime.map((row) => row.displayName).join(', ')}</div>}
      <div className="flex items-center gap-3 mt-3 text-[11px] text-muted">
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-accent" /> today</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-accent/60" /> this week</span>
        <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-400" /> no time logged today</span>
      </div>
    </section>
  );
}

/* ─── My-day extras (Staff) ──────────────────────────────────────────────── */

function MyHoursSection({ today, week }: { today: number; week: number }) {
  return (
    <section className="bg-bg-panel border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-medium text-white">My hours</h2>
        <Link to="/time" className="text-xs text-accent hover:underline inline-flex items-center gap-1">
          Log time <ArrowRight size={12} />
        </Link>
      </div>
      <div className="flex items-end gap-8">
        <div>
          <div className="text-xl sm:text-2xl font-semibold text-emerald-300">{formatHours(today)}</div>
          <div className="text-xs text-muted mt-0.5">today</div>
        </div>
        <div>
          <div className="text-xl sm:text-2xl font-semibold text-white">{formatHours(week)}</div>
          <div className="text-xs text-muted mt-0.5">this week</div>
        </div>
      </div>
    </section>
  );
}

/* ─── Shared bits ────────────────────────────────────────────────────────── */

function ItemRow({ item, overdue }: { item: DashboardItemRow; overdue?: boolean }) {
  const assignees = [
    item.assignee1Name ? `A1 ${item.assignee1Name}` : null,
    item.assignee2Name ? `A2 ${item.assignee2Name}` : null,
    item.managerName ? `mgr ${item.managerName}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <li className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
      <div className="min-w-0">
        <Link to={`/compliance/${item.id}`} className="text-sm text-white hover:text-accent block truncate">
          {item.serviceType}
        </Link>
        <p className="text-xs text-muted truncate">
          {item.clientName} {item.jurisdiction ? `· ${item.jurisdiction}` : ''}
        </p>
        {assignees && <p className="text-xs text-muted truncate">{assignees}</p>}
      </div>
      <div className="sm:text-right flex-shrink-0">
        <div className={`text-xs ${overdue ? 'text-rose-400 font-medium' : 'text-white'}`}>
          {item.dueDate ? fmtDate(item.dueDate) : 'No date'}
        </div>
        <span className={statusBadge(item.status)}>{item.status}</span>
      </div>
    </li>
  );
}

function isOverdue(item: DashboardItemRow): boolean {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return item.dueDate != null && new Date(item.dueDate).getTime() < today.getTime();
}

function timeOfDay(): string {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 18) return 'afternoon';
  return 'evening';
}

function statusBadge(status: string): string {
  const classes = STATUS_BADGE_CLASSES[status as keyof typeof STATUS_BADGE_CLASSES];
  return classes ?? 'px-2 py-0.5 rounded text-xs bg-bg-hover text-white';
}

function formatHours(hours: number): string {
  return `${Number(hours.toFixed(1))}h`;
}

